import { useTranslation } from 'react-i18next'
import React, { useMemo, useCallback, useRef, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useFeatureFlags } from '@fs/zion-flags'
import { Alert, Paragraph, FlowGrid } from '@fs/zion-ui'
import ZionForm, { useFieldArray, useZionForm, useWatch, useFormState } from '@fs/zion-form'
import {
  generateSls,
  saveSlsAndUpdateState,
  useSlsStateContext,
  useSlsDispatchContext,
  useSlsHighlightsContext,
  getPersonName,
} from '@fs/zion-sls'
import { yupResolver } from '@hookform/resolvers/yup'
import useFamilyReviewRelationshipOptions from '../../../views/familyReview/hooks/useFamilyReviewRelationshipOptions'
import { prepareFamilyMembersDefaultValues, getGroupedGenderedRelTypeSuggestions } from './utils/familyMember.utils'
import {
  prepareFamilyMemberNamesAutosuggest,
  getPersonIdsToReviewFromCreateActions,
  getHouseholdSuggestions,
} from '../shared/relationships/relationshipQueries'
import CardFooter from '../shared/components/CardFooter'
import getRelationshipDispatches from '../shared/relationships/saveRelationships'
import { MAKE_NEW_RECORD } from '../shared/relationships/relationshipConstants'
import { FullTextApp, headers } from '../../../../shared/services/config'
import { errorCollector } from '../../../../shared/utils/utils'
import { useOverlayContext } from '../../../../shared/providers/overlayContextProvider/OverlayContext'
import { collectHighlightIdsForRelationships } from '../../../views/indexing/components/editPanels/editRelationships/utils/EditRelationships.utls'
import { householdDetailsValidationSchema } from '../shared/relationships/validationSchema'
import RelationshipInputs from './components/RelationshipInputs'

/**
 * FamilyMembersCardEditMode
 *
 * Provides an editable form interface for managing a person's family member relationships.
 * Each relationship row supports selecting a relationship type for each household member
 * (with autosuggest). Existing rows can be removed. The form integrates with
 * SLS (Shared Linked State) actions to persist relationship changes.
 *
 * Behavior & Flow:
 *  - Default form values are prepared from the incoming `relationships` prop.
 *  - On submit, differences are calculated and converted into SLS dispatches which are saved
 *    via `saveSlsAndUpdateState`. Errors are captured and surfaced with an Alert.
 *  - If the card has not yet been reviewed, submitting marks it reviewed via `setReviewed(true)`.
 *
 * @component
 * @param {Object}   props
 * @param {Object}   props.person                       The primary person SLS person object (must include `id` and `recordId`).
 * @param {Array}    props.allRecords                   Array of all RECORD type SLS objects in the current image.
 * @param {Array}    props.relationships                Array of existing relationship objects used for defaults.
 * @param {boolean}  [props.reviewed=false]             Whether the relationships section has been reviewed.
 * @param {Function} props.setReviewed                  Callback to set reviewed state in parent.
 * @param {Function} props.setIsEditing                 Toggles the parent card edit mode off after save / cancel.
 * @param {string}   props.userId                       Current user id for audit purposes passed to save.
 * @param {Function} [props.onSave]                     Callback invoked after successful save with form data.
 * @param {Function} [props.onDirtyChange=(isDirty) => {}] Callback function invoked when the form dirty state changes.
 * @param {boolean}  [props.saveCardDataToSls=false]    Whether to save changes to SLS backend.
 * @param {string}   [props.appName=FullTextApp]        The application name for SLS operations.
 * @param {string}   [props.splitScreenPartitionKey]    Split screen partition key if using split screen.
 * @param {boolean}  [props.isCreatingNewPerson=false]  Whether the card is being used in the context of creating a new person (vs editing an existing one).
 *
 * @returns {JSX.Element}
 */
const FamilyMembersCardEditMode = ({
  person,
  allRecords,
  relationships,
  reviewed = false,
  setReviewed,
  setIsEditing,
  userId,
  onSave = () => {},
  onDirtyChange = () => {},
  saveCardDataToSls = false,
  splitScreenPartitionKey,
  appName = FullTextApp,
  isCreatingNewPerson = false,
}) => {
  const [t] = useTranslation()
  const { types, images, currentImageArk } = useSlsStateContext()
  const h = useSlsHighlightsContext()
  const {
    rip_sharedImageViewer_saveChanges: saveChangesFlag,
    rip_sharedImageViewer_sls_actions_splunk_debugger: slsActionsSplunkDebugger,
  } = useFeatureFlags(['rip_sharedImageViewer_saveChanges', 'rip_sharedImageViewer_sls_actions_splunk_debugger'])

  const householdSuggestions = useMemo(() => {
    return getHouseholdSuggestions({ records: allRecords, person })
  }, [allRecords, person])

  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const slsDispatch = useSlsDispatchContext()
  const nameAutosuggestRefs = useRef([])
  const { topMostPanel } = useOverlayContext()
  const { setDisplayHighlights, getHighlights } = useSlsHighlightsContext()

  const relationshipSuggestions = useMemo(() => {
    return getGroupedGenderedRelTypeSuggestions(types, undefined, true)
  }, [types])

  const defaultValues = useMemo(() => {
    return prepareFamilyMembersDefaultValues(relationships, person, householdSuggestions, isCreatingNewPerson)
  }, [relationships, person, householdSuggestions, isCreatingNewPerson])

  const formMethods = useZionForm({
    mode: 'all',
    defaultValues,
    resolver: yupResolver(householdDetailsValidationSchema),
  })

  const fieldArrayMethods = useFieldArray({
    name: 'relationships',
    control: formMethods.control,
  })

  const { fields } = fieldArrayMethods

  const watchedFormValues = useWatch({
    control: formMethods.control,
  })

  const imageNamesAutoCompleteSuggestions = useFamilyReviewRelationshipOptions({
    images,
    mainPartitionKey: currentImageArk,
    splitScreenPartitionKey,
    primaryPersonId: person?.id,
    currentRecordId: watchedFormValues.targetRecordId || person?.recordId,
    includeRecordId: true,
  })

  const namesAutoSuggestions = prepareFamilyMemberNamesAutosuggest({
    relationships,
    namesAutoSuggest: imageNamesAutoCompleteSuggestions,
  })

  // Track form dirty state to inform parent component so it can trigger unsaved changes overlay
  const { isDirty } = useFormState({ control: formMethods.control })

  const topMostPanelId = useMemo(() => topMostPanel(), [topMostPanel])

  // Removed isButtonDisabled logic to comply with RIP-4674:
  // Buttons should never be greyed out based on validation.
  // Validation errors will be shown when the user clicks Save/Next.

  const handleSuccess = useCallback(() => {
    if (!reviewed) {
      setReviewed(true)
    }
    setIsEditing(false)
    setSaving(false)
  }, [reviewed, setReviewed, setIsEditing])

  const saveDispatches = useCallback(
    async (data, reviewDispatches) => {
      setSaving(true)
      setError(null)

      try {
        // Generate dispatches - this will throw if validation fails
        // Pass all form data including recordGroup for cross-record handling
        const newPersonAndRelationshipDispatches = await getRelationshipDispatches({
          formData: data,
          defaultValues,
          person,
          images,
          appName,
          reviewDispatches,
          currentPartitionKey: currentImageArk,
        })

        // Get highlight dispatches from the highlights context
        const highlightDispatches = h.getDispatches({
          mainImageArk: currentImageArk,
          splitScreenImageArk: splitScreenPartitionKey,
        })

        // Combine all dispatches
        const allDispatches = [...newPersonAndRelationshipDispatches, ...highlightDispatches]

        const personIdsToReview = getPersonIdsToReviewFromCreateActions(allDispatches)
        const sls = generateSls(allDispatches)

        if (saveCardDataToSls) {
          saveSlsAndUpdateState({
            sls,
            dispatches: allDispatches,
            saveChangesFlag,
            slsActionsSplunkDebugger,
            userId,
            dispatch: slsDispatch,
            appName,
            fsUserAgentChain: headers['FS-User-Agent-Chain'],
            errorHandler: (err) => {
              setError(err)
              errorCollector(err, null, {
                codeId: 90,
                sls,
                status: err?.response?.status,
                responseBody: err?.response?.data,
              })
              setSaving(false)
            },
            successHandler: () => {
              // Processing SLS dispatches to update local state even if the saveChangesFlag is off
              if (!saveChangesFlag.isOn) {
                slsDispatch({
                  type: 'BATCH_DISPATCHES',
                  value: newPersonAndRelationshipDispatches,
                })
              }

              setSaving(false)
              // The AddNamesPanel needs personIdsToReview to add newly created people to the review list.
              onSave({
                formValues: data,
                personIdsToReview,
              })
              h.commitEdits()
            },
            saveInitiatorFilename: 'FamilyMembersCardEditMode.js',
          })
        } else {
          setSaving(false)
          onSave({
            dispatches: allDispatches,
            sls,
            formValues: data,
            personIdsToReview,
          })
          h.commitEdits()
        }
      } catch (err) {
        // Validation errors (duplicate relationships, missing data, etc.)
        // eslint-disable-next-line no-console
        setError(err)
        errorCollector(err, null, {
          codeId: 91,
          message: 'Relationship validation error',
        })
        setSaving(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      appName,
      defaultValues,
      handleSuccess,
      images,
      onSave,
      saveCardDataToSls,
      saveChangesFlag,
      slsActionsSplunkDebugger,
      person,
      slsDispatch,
      userId,
      currentImageArk,
      splitScreenPartitionKey,
    ]
  )

  const onSubmit = useCallback(
    async (data) => {
      // Ensure household has a value - default to MAKE_NEW_RECORD if cleared
      if (!data?.household?.key) {
        const defaultHousehold = householdSuggestions.find((hh) => hh.key === MAKE_NEW_RECORD)
        if (defaultHousehold) {
          data = { ...data, household: defaultHousehold, targetRecordId: defaultHousehold.key }
          formMethods.setValue('household', defaultHousehold, { shouldDirty: true })
          formMethods.setValue('targetRecordId', defaultHousehold.key, { shouldDirty: true })
        }
      }

      // Only proceed if form is dirty OR person is floating
      if (isDirty) {
        // Auto-assign primary for single-person household
        const activeRelationships = data.relationships.filter((rel) => !rel.isDeleted && rel.personId)
        if (activeRelationships.length === 0 && !data.targetPersonIsPrimary) {
          formMethods.setValue('targetPersonIsPrimary', true, { shouldDirty: true })
          data = { ...data, targetPersonIsPrimary: true }
        }

        await saveDispatches(data, [])
      } else {
        setSaving(false)
        onSave({
          formValues: data,
        })
        h.commitEdits()
      }
    },
    [isDirty, formMethods, saveDispatches, setSaving, onSave, h, householdSuggestions]
  )

  const handleCancel = useCallback(() => {
    formMethods.reset()
    onDirtyChange(false)
    setIsEditing(false)
  }, [formMethods, setIsEditing, onDirtyChange])

  useEffect(() => {
    onDirtyChange(isDirty)
  }, [isDirty, onDirtyChange])

  useEffect(() => {
    if (topMostPanelId !== 'highlightsPanel' && currentImageArk) {
      const highlights = getHighlights()
      const { allPeopleHighlights, allPeopleReferenceHighlights } = collectHighlightIdsForRelationships(
        watchedFormValues,
        person.id,
        highlights.mainEditHighlights,
        highlights.splitScreenEditHighlights
      )

      setDisplayHighlights({
        highlights: [...allPeopleHighlights, ...allPeopleReferenceHighlights],
        mainPartitionKey: currentImageArk,
        splitScreenPartitionKey,
        fieldTypes: ['NAME_SURN', 'NAME_SUF', 'NAME_GN'],
        customFilter: (highlight) => highlight.personIds.length > 0,
      })
    }
  }, [
    currentImageArk,
    splitScreenPartitionKey,
    watchedFormValues,
    person.id,
    topMostPanelId,
    setDisplayHighlights,
    getHighlights,
  ])

  return (
    <ZionForm methods={formMethods} onSubmit={onSubmit} id="household-card-form">
      <FlowGrid orientation="vertical" columnCount={1} gutters="xxs">
        <Paragraph size="sm">
          {t(
            'household-details-card.relationships-description',
            'The following individuals are in the same household. How are they related to {personName}?',
            { personName: getPersonName({ person }) }
          )}
        </Paragraph>

        <Paragraph size="xs">
          {t('household-details-card.relationships-tip', 'Tip: Select No Relation if they are not related')}
        </Paragraph>

        {fields.map((field, index) => (
          <RelationshipInputs
            key={field?.id}
            field={field}
            index={index}
            relationshipSuggestions={relationshipSuggestions}
            namesAutoSuggestions={namesAutoSuggestions}
            formMethods={formMethods}
            fieldArrayMethods={fieldArrayMethods}
            refs={nameAutosuggestRefs}
          />
        ))}

        <CardFooter reviewed={reviewed} onCancel={handleCancel} saving={saving} label="family-members" />
        {error && (
          <Alert type="error">
            {t('household-details-card.save-error-msg', 'Error saving data. Please try again later.')}
          </Alert>
        )}
      </FlowGrid>
    </ZionForm>
  )
}

FamilyMembersCardEditMode.propTypes = {
  person: PropTypes.shape({
    // Required properties
    id: PropTypes.string.isRequired,
    // recordId can be null for floating persons (persons not yet attached to a record)
    recordId: PropTypes.string,
  }),

  relationships: PropTypes.arrayOf(
    PropTypes.shape({
      relKey: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.oneOf([null])]),
      otherPersonId: PropTypes.string.isRequired,
    })
  ),

  reviewed: PropTypes.bool,
  saveCardDataToSls: PropTypes.bool,
  splitScreenPartitionKey: PropTypes.string,
}

FamilyMembersCardEditMode.defaultProps = {
  person: { id: '', recordId: null },
  relationships: [],
  reviewed: false,
  saveCardDataToSls: false,
}

export default FamilyMembersCardEditMode
